import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  Music2,
  Target,
  ListChecks,
  Sparkles,
  MessageSquare,
  UserCheck,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Baby,
  Activity
} from 'lucide-react';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileOpen]);

  const navLinks = [
    { to: '/admin-dashboard', icon: Home, text: 'Dashboard', color: 'from-[#7FE5D3] to-[#5FD3C1]' },
    { to: '/admin-dashboard/users', icon: Users, text: 'User Management', color: 'from-[#FDB5C8] to-[#FD9AB5]' },
    { to: '/admin-dashboard/manage-bands', icon: Music2, text: 'Band Management', color: 'from-[#B8C5FF] to-[#A0B0FF]' },
    { to: '/admin-dashboard/pregnancy-tasks', icon: Baby, text: 'Pregnancy Tasks', color: 'from-[#B8C5FF] to-[#A0B0FF]' },
    { to: '/admin-dashboard/milestones', icon: Target, text: 'Milestones', color: 'from-[#FFD88A] to-[#FFC870]' },
    { to: '/admin-dashboard/manage-weekly-checklist', icon: ListChecks, text: 'Weekly Checklist', color: 'from-[#E8F5B2] to-[#D5E89F]' },
    { to: '/admin-dashboard/manage-recommended-activities', icon: Sparkles, text: 'Activities', color: 'from-[#D4A5FF] to-[#C18FFF]' },
    { to: '/admin-dashboard/admin-concern', icon: MessageSquare, text: 'Concerns', color: 'from-[#FF9B9B] to-[#FF8080]' },
    { to: '/admin-dashboard/expert-manager', icon: UserCheck, text: 'Expert Management', color: 'from-[#A8E6CF] to-[#8FD9B6]' },
    { to: '/admin-dashboard/test-services', icon: Activity, text: 'Test Services', color: 'from-[#A0C4FF] to-[#80AAFF]' },
  ];

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to logout?')) return;

    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL;
      const refreshToken = localStorage.getItem('refreshToken');

      if (refreshToken) {
        await fetch(`${apiUrl}/accounts/logout/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh: refreshToken }),
        });
      }
    } catch (error) {
      console.error('Failed to logout:', error);
    } finally {
      localStorage.clear();
      navigate('/auth');
    }
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-[60] p-3 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300"
      >
        {isMobileOpen ? (
          <X className="w-6 h-6 text-[#1e3a5f]" />
        ) : (
          <Menu className="w-6 h-6 text-[#1e3a5f]" />
        )}
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          onClick={() => setIsMobileOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-[55]"
        />
      )}

      {/* Sidebar Container - Always fixed */}
      <div className={`fixed inset-y-0 left-0 z-[56] transition-all duration-300 ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isCollapsed ? 'lg:w-20' : 'lg:w-72'} w-72`}>

        {/* Sidebar Content */}
        <aside className="h-full bg-white flex flex-col shadow-xl lg:shadow-lg overflow-hidden">

          {/* Header - Fixed at top */}
          <div className={`flex-shrink-0 p-6 ${isCollapsed ? 'lg:px-4' : ''} border-b border-gray-100 bg-white`}>
            <div className="flex items-center justify-between">
              <div className={`flex items-center ${isCollapsed ? 'lg:justify-center lg:flex-1' : 'gap-3'}`}>
                <div className={`${isCollapsed ? 'lg:w-12 lg:h-12' : 'w-14 h-14'} rounded-2xl bg-gradient-to-br from-[#FDB913] to-[#FFA500] p-2.5 shadow-lg transition-all duration-300`}>
                  <img
                    src="/TiflLogo.png"
                    alt="TIFL"
                    className="w-full h-full object-contain filter brightness-0 invert"
                  />
                </div>
                {!isCollapsed && (
                  <div className="hidden lg:block">
                    <h2 className="text-lg font-bold text-[#1e3a5f]">TIFL Admin</h2>
                    <p className="text-xs text-gray-500">Management Panel</p>
                  </div>
                )}
              </div>
              <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="hidden lg:flex w-8 h-8 items-center justify-center rounded-xl bg-gray-50 hover:bg-gray-100 transition-all"
              >
                {isCollapsed ? (
                  <ChevronRight className="w-4 h-4 text-[#1e3a5f]" />
                ) : (
                  <ChevronLeft className="w-4 h-4 text-[#1e3a5f]" />
                )}
              </button>
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4 space-y-2 custom-scrollbar">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/admin-dashboard'}
                onClick={() => setIsMobileOpen(false)}
                className={({ isActive }) =>
                  `group relative flex items-center ${isCollapsed ? 'lg:justify-center' : ''
                  } p-4 rounded-2xl transition-all duration-300 ${isActive
                    ? 'bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white shadow-lg'
                    : 'hover:bg-gray-50 text-gray-600 hover:text-[#1e3a5f]'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div className={`relative flex items-center justify-center ${isCollapsed ? '' : 'mr-3'}`}>
                      <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${link.color} opacity-0 group-hover:opacity-20 transition-opacity duration-300`}></div>
                      <link.icon className={`relative w-5 h-5 ${isActive ? 'text-white' : 'text-[#1e3a5f]'} transition-colors`} />
                    </div>

                    {!isCollapsed && (
                      <span className="font-medium hidden lg:block">{link.text}</span>
                    )}

                    <span className="font-medium lg:hidden">{link.text}</span>

                    {isActive && !isCollapsed && (
                      <div className="hidden lg:block absolute right-4 w-2 h-2 rounded-full bg-white animate-pulse"></div>
                    )}

                    {isCollapsed && (
                      <div className="hidden lg:block absolute left-full ml-3 px-3 py-2 bg-[#1e3a5f] text-white text-sm rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[70]">
                        {link.text}
                        <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-[#1e3a5f] rotate-45"></div>
                      </div>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* User Section & Logout - Fixed at bottom */}
          <div className="flex-shrink-0 p-4 border-t border-gray-100 bg-white">
            {!isCollapsed && (
              <div className="hidden lg:flex items-center p-3 mb-3 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FDB913] to-[#FFA500] flex items-center justify-center text-white font-bold shadow-md">
                  A
                </div>
                <div className="ml-3 min-w-0">
                  <p className="text-sm font-semibold text-[#1e3a5f] truncate">Admin User</p>
                  <p className="text-xs text-gray-500">Administrator</p>
                </div>
              </div>
            )}

            <button
              onClick={handleLogout}
              className={`group relative flex items-center ${isCollapsed ? 'lg:justify-center' : ''
                } w-full p-4 rounded-2xl bg-[#FDE9EB] hover:bg-red-50 text-gray-700 hover:text-red-600 transition-all duration-300`}
            >
              <LogOut className={`w-5 h-5 ${isCollapsed ? '' : 'mr-3'} transition-transform group-hover:-translate-x-0.5`} />
              {!isCollapsed && (
                <span className="font-medium hidden lg:block">Logout</span>
              )}
              <span className="font-medium lg:hidden">Logout</span>

              {isCollapsed && (
                <div className="hidden lg:block absolute left-full ml-3 px-3 py-2 bg-red-500 text-white text-sm rounded-xl shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-[70]">
                  Logout
                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-red-500 rotate-45"></div>
                </div>
              )}
            </button>
          </div>
        </aside>
      </div>

      {/* Spacer for main content - only on desktop */}
      <div className={`hidden lg:block transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-72'}`} />

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #e5e7eb;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #d1d5db;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
      `}</style>
    </>
  );
};

export default Sidebar;